package br.com.vvrefeicoes.portal.core.util

import br.com.vvrefeicoes.portal.domain.model.MonthPeriod

enum class PeriodMode {
    MONTH,
    YEAR,
    TOTAL,
    CUSTOM,
}

data class PeriodSelection(
    val mode: PeriodMode,
    val month: MonthPeriod,
    val year: Int,
    val start: MonthPeriod,
    val end: MonthPeriod,
) {
    val isRange: Boolean
        get() = mode != PeriodMode.MONTH

    val months: List<MonthPeriod>
        get() =
            when (mode) {
                PeriodMode.MONTH -> listOf(month)
                PeriodMode.YEAR -> (1..12).map { MonthPeriod(year, it) }
                PeriodMode.TOTAL -> emptyList()
                PeriodMode.CUSTOM -> monthsBetween(start, end)
            }

    val endMonth: MonthPeriod
        get() =
            when (mode) {
                PeriodMode.MONTH -> month
                PeriodMode.YEAR -> MonthPeriod(year, 12)
                PeriodMode.TOTAL -> month
                PeriodMode.CUSTOM -> end
            }

    val label: String
        get() =
            when (mode) {
                PeriodMode.MONTH -> MonthCalendar.label(month)
                PeriodMode.YEAR -> year.toString()
                PeriodMode.TOTAL -> "Total"
                PeriodMode.CUSTOM -> "${MonthCalendar.label(start)} – ${MonthCalendar.label(end)}"
            }

    fun window(defaultMonths: Int): List<MonthPeriod> {
        if (isRange) return months.takeLast(MAX_HISTORY_MONTHS)
        val anchor = month
        return (defaultMonths - 1 downTo 0).map { offset -> monthsBefore(anchor, offset) }
    }

    private fun monthsBefore(
        period: MonthPeriod,
        months: Int,
    ): MonthPeriod {
        var year = period.year
        var month = period.month - months
        while (month < 1) {
            month += 12
            year -= 1
        }
        return MonthPeriod(year = year, month = month)
    }

    companion object {
        const val HISTORY_MONTHS = 6
        const val MAX_HISTORY_MONTHS = 12

        fun current(period: MonthPeriod): PeriodSelection =
            PeriodSelection(
                mode = PeriodMode.MONTH,
                month = period,
                year = period.year,
                start = period,
                end = period,
            )
    }
}

private fun monthsBetween(
    start: MonthPeriod,
    end: MonthPeriod,
): List<MonthPeriod> {
    val result = mutableListOf<MonthPeriod>()
    var current = start
    var guard = 0
    while ((current.year < end.year || (current.year == end.year && current.month <= end.month)) && guard < 600) {
        result += current
        current = current.next()
        guard++
    }
    return result.ifEmpty { listOf(start) }
}
